import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoggingModule, LoggingModuleOptions } from 'fitforce-logging';
import { GenericRestService, OfflineFirstModule } from 'fitforce-offline-first';
import { GuidedTourModule, GuidedTourService } from 'ngx-guided-tour';
import {
  AddPermissionDialogComponent,
  DeletePermissionDialogComponent,
  ManagePermissionsComponent,
} from './components/manage-users/manage-permissions/manage-permissions.component';
import {
  AddRoleDialogComponent,
  DeleteRoleDialogComponent,
  LimitPipe,
  ManageRolesComponent,
} from './components/manage-users/manage-roles/manage-roles.component';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MdePopoverModule } from '@material-extended/mde';
import { NgxsModule } from '@ngxs/store';
import { RestOptions } from 'fitforce-document-sync';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AlertDialogComponent } from './components/alert-dialog/alert-dialog.component';
import { ChangePasswordDialogComponent } from './components/change-password-dialog/change-password-dialog.component';
import { ChartDialogComponent } from './components/chart-dialog/chart-dialog.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { DeleteConfirmationDialogComponent } from './components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { DodNoticeAndConsentBannerComponent } from './components/dod-notice-and-consent-banner/dod-notice-and-consent-banner.component';
import { EditProfileDialogComponent } from './components/edit-profile-dialog/edit-profile-dialog.component';
import { ImportDatabaseDialogComponent } from './components/import-database-dialog/import-database-dialog.component';
import { InformedConsentDialogComponent } from './components/informed-consent-dialog/informed-consent-dialog.component';
import { InitializeLoadScreenComponent } from './components/initialize-load-screen/initialize-load-screen.component';
import { LoginComponent } from './components/login/login.component';
import { ManageUserDialogComponent } from './components/manage-user-dialog/manage-user-dialog.component';
import { ManageUnapprovedUsersComponent } from './components/manage-users/manage-unapproved-users/manage-unapproved-users.component';
import { ManageUsersComponent } from './components/manage-users/manage-users.component';
import { NavUserMenuComponent } from './components/nav-user-menu/nav-user-menu.component';
import { PlanningComponent } from './components/planning/planning.component';
import { RedirectDialogComponent } from './components/redirect-dialog/redirect-dialog.component';
import { SyncStatusComponent } from './components/sync-status/sync-status.component';
import { UserSearchComponent } from './core/components/user-search/user-search.component';
import { CoreModule } from './core/core.module';
import { CustomMaterialModule } from './custom-material/custom-material.module';
import { DocumentModule } from './document/document.module';
import { ExerciseProgramModule } from './exercise-program/exercise-program.module';
import { ExploreModule } from './explore/explore.module';
import { ExploreState } from './explore/state/explore.state';
import { GroupManagementModule } from './group-management/group-management.module';
import { GroupManagementState } from './group-management/state/group-management.state';
import { StartTourDialogComponent } from './guided-tour/components/start-tour-dialog/start-tour-dialog.component';
import { GuidedTourStateModel } from './guided-tour/models/guided-tour-state.model';
import { GuidedTourState } from './guided-tour/state/guided-tour.state';
import { ArraySortPipe } from './pipes/array-sort.pipe';
import { FilterGroupsPipe } from './pipes/filter-groups.pipe';
import { FilterUsersPipe } from './pipes/filter-users.pipe';
import { SearchFilterPlanPipe } from './pipes/search-filter-plan.pipe';
import { AuthInterceptor } from './services/auth-interceptor.service';
import { AuthService } from './services/auth.service';
import { ConfirmPasswordDirective } from './services/confirm-password.directive';
import { CreateRestService } from './services/create.rest.service';
import { ErrorInterceptorService } from './services/error-interceptor.service';
import { GroupDataService } from './services/group-data.service';
import { GroupsRestService } from './services/groups.rest.service';
import { GuidedTourDataService } from './services/guided-tour-data.service';
import { ExtendSessionComponent } from './components/extend-session/extend-session.component';

const pipes = [SearchFilterPlanPipe, ArraySortPipe, LimitPipe];

const httpInterceptors = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptorService,
    multi: true,
  },
];

const LOGGING_OPTIONS: LoggingModuleOptions = {
  logLevel: environment.logLevel,
};

@NgModule({
  declarations: [
    AppComponent,
    SearchFilterPlanPipe,
    DashboardComponent,
    PlanningComponent,
    LoginComponent,
    ConfirmationDialogComponent,
    ConfirmPasswordDirective,
    ChangePasswordDialogComponent,
    AlertDialogComponent,
    FilterGroupsPipe,
    FilterUsersPipe,
    EditProfileDialogComponent,
    InitializeLoadScreenComponent,
    ...pipes,
    ImportDatabaseDialogComponent,
    NavUserMenuComponent,
    SyncStatusComponent,
    InformedConsentDialogComponent,
    UserSearchComponent,
    ChartDialogComponent,
    StartTourDialogComponent,
    DodNoticeAndConsentBannerComponent,
    ManageUsersComponent,
    ManageUserDialogComponent,
    DeleteConfirmationDialogComponent,
    ManageRolesComponent,
    ManagePermissionsComponent,
    AddPermissionDialogComponent,
    DeletePermissionDialogComponent,
    AddRoleDialogComponent,
    DeleteRoleDialogComponent,
    ManageUnapprovedUsersComponent,
    RedirectDialogComponent,
    ExtendSessionComponent,
  ],
  imports: [
    ServiceWorkerModule.register('./ngsw-worker.js', {
      enabled: environment.production,
    }),
    BrowserModule,
    BrowserAnimationsModule,
    CustomMaterialModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    AppRoutingModule,
    MdePopoverModule,
    PerfectScrollbarModule,
    LoggingModule.forRoot(LOGGING_OPTIONS),
    DragDropModule,
    DocumentModule,
    OfflineFirstModule,
    ScrollingModule,
    NgxsModule.forRoot([ExploreState, GroupManagementState, GuidedTourState], {
      developmentMode: !environment.production,
    }),
    GroupManagementModule,
    CoreModule,
    ExploreModule,
    ExerciseProgramModule,
    MatIconModule,
    MatButtonModule,
    GuidedTourModule,
    MatDialogModule,
  ],
  exports: [],
  providers: [
    GenericRestService,
    AuthService,
    httpInterceptors,
    GroupDataService,
    { provide: RestOptions, useValue: environment },
    DeviceDetectorService,
    CreateRestService,
    GroupsRestService,
    GuidedTourService,
    GuidedTourDataService,
    GuidedTourStateModel,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
